import type { MatrixClient, Room } from 'matrix-js-sdk';
import { decodeEntities } from '@/lib/format';
import type { ChatMemberRole, ChatRoomMember } from '@/types/chat';

// Адаптер: кастомные Matrix state-events комнаты → модель для UI (заголовок диалога, строка списка).
// Типы событий и поля сверены с ЖИВОЙ комнатой сделки (см. P2-разведку state-events).

const EV = {
  members: 'com.graintrade.state.room.members',
  order: 'com.graintrade.state.order',
  bargain: 'com.graintrade.state.room.bargain',
  sale: 'com.graintrade.state.room.sale',
  purchase: 'com.graintrade.state.room.purchase',
} as const;

interface GoodsMainProperty {
  readonly name?: string;
  readonly prefix?: string;
  readonly postfix?: string;
  readonly value?: string;
}
interface PriceInfo {
  readonly fcaPricePerTonWithoutNds?: string | number;
  readonly cptPricePerTonWithoutNds?: string | number;
}
interface PowerLevels {
  readonly users?: Record<string, number>;
  readonly users_default?: number;
  readonly events?: Record<string, number>;
  readonly events_default?: number;
}

export interface RoomDescriptor {
  readonly bargainId?: number;
  readonly specNumber?: string;
  readonly goodsType?: string;
  /** «Протеин ≥ 15.3 %» из goodsMainProperty. */
  readonly quality?: string;
  readonly weightTon?: number;
  /** Цена по роли торгующей стороны: покупатель → CPT, продавец → FCA (инвариант платформы). */
  readonly pricePerTon?: number;
  readonly priceLabel?: 'CPT' | 'FCA';
  readonly toAddressTitle?: string;
  readonly distanceKm?: number;
  readonly myRole?: ChatMemberRole;
  /** Контрагент в комнате (для агента — покупатель/продавец; для покупателя/продавца — агент). */
  readonly counterparty?: ChatRoomMember;
  /** Все участники по matrixId — для резолва имени/роли/аватара отправителя реплики. */
  readonly membersById: Record<string, ChatRoomMember>;
  /** Можно ли отправлять (false при завершённой сделке — power-level поднят). */
  readonly writable: boolean;
}

// ── Чистые хелперы (тестируемые отдельно от matrix-объектов) ──────────────────

const toNum = (v: unknown): number | undefined => {
  const n = typeof v === 'number' ? v : typeof v === 'string' ? Number(v) : NaN;
  return Number.isFinite(n) ? n : undefined;
};

/** «Протеин ≥ 15.3 %» (prefix `&ge;` → ≥, postfix `%`). */
export function formatRoomQuality(prop: GoodsMainProperty | undefined): string | undefined {
  if (!prop?.name) {
    return undefined;
  }
  const prefix = prop.prefix ? `${decodeEntities(prop.prefix)} ` : '';
  const postfix = prop.postfix ? ` ${prop.postfix}` : '';
  return `${prop.name} ${prefix}${prop.value ?? ''}${postfix}`.trim();
}

/** Цена + ярлык по роли торгующей стороны (не агента). */
export function priceByTradingRole(
  priceInfo: PriceInfo | undefined,
  tradingRole: ChatMemberRole | undefined,
): { pricePerTon?: number; priceLabel?: 'CPT' | 'FCA' } {
  if (tradingRole === 'buyer') {
    return { pricePerTon: toNum(priceInfo?.cptPricePerTonWithoutNds), priceLabel: 'CPT' };
  }
  if (tradingRole === 'seller') {
    return { pricePerTon: toNum(priceInfo?.fcaPricePerTonWithoutNds), priceLabel: 'FCA' };
  }
  return {};
}

/** Право отправки: мой power-level ≥ требуемого для `m.room.message`. */
export function computeWritable(power: PowerLevels | undefined, myUserId: string): boolean {
  const myPower = power?.users?.[myUserId] ?? power?.users_default ?? 0;
  const msgPower = power?.events?.['m.room.message'] ?? power?.events_default ?? 0;
  return myPower >= msgPower;
}

/**
 * Собеседник в комнате: для агента — торгующая сторона, для стороны — агент.
 *
 * Выбирать «первого участника, который не я», нельзя: сторона представлена организацией, и рядом
 * со мной в комнате находятся мои же коллеги. Такой выбор ставил в заголовок случайного коллегу
 * с его ролью — то есть продавец видел диалог, подписанный «Продавец», вместо агента.
 *
 * Комнаты без ролей (созданные до перехода на агентскую модель) сохраняют прежнее поведение.
 */
export function counterpartyOf(
  members: Record<string, ChatRoomMember> | undefined,
  myUserId: string,
): ChatRoomMember | undefined {
  const others = Object.entries(members ?? {}).filter(([id]) => id !== myUserId);
  const myRole = members?.[myUserId]?.role;
  const wanted = (m: ChatRoomMember): boolean =>
    myRole === 'agent' ? m.role === 'buyer' || m.role === 'seller' : m.role === 'agent';
  return (others.find(([, m]) => wanted(m)) ?? others[0])?.[1];
}

/** Торгующая роль среди участников (buyer|seller — не agent). */
export function tradingRoleOf(members: Record<string, ChatRoomMember> | undefined): ChatMemberRole | undefined {
  for (const m of Object.values(members ?? {})) {
    if (m.role === 'buyer' || m.role === 'seller') {
      return m.role;
    }
  }
  return undefined;
}

// ── describeRoom: чтение state-events из Room ────────────────────────────────

function stateContent(room: Room, type: string): Record<string, unknown> | undefined {
  const ev = room.currentState.getStateEvents(type, '');
  return ev ? (ev.getContent() as Record<string, unknown>) : undefined;
}

/**
 * Комната прямого общения покупателя с продавцом — то есть любая, где нет участника с ролью агента.
 *
 * Под это подпадают и прямые комнаты сделок, и комнаты-отклики на предложения: по правилу «общение
 * только через агента» ни те, ни другие больше не создаются, а уже существующие не показываются.
 * Признак — отсутствие агента среди участников, а не вид комнаты: он один и тот же для обоих случаев
 * и не требует перечислять виды, которые могут появиться позже.
 */
export function isDirectRoom(room: Room): boolean {
  const members = stateContent(room, EV.members) as Record<string, ChatRoomMember> | undefined;
  return !Object.values(members ?? {}).some((m) => m.role === 'agent');
}

export function describeRoom(client: MatrixClient, room: Room): RoomDescriptor {
  const myUserId = client.getUserId() ?? '';
  const members = stateContent(room, EV.members) as Record<string, ChatRoomMember> | undefined;
  const order = stateContent(room, EV.order);
  const bargain = stateContent(room, EV.bargain);
  const sale = stateContent(room, EV.sale) ?? stateContent(room, EV.purchase);
  const power = room.currentState.getStateEvents('m.room.power_levels', '')?.getContent() as
    | PowerLevels
    | undefined;

  const myRole = members?.[myUserId]?.role;
  const counterparty = counterpartyOf(members, myUserId);
  const tradingRole = tradingRoleOf(members);
  const price = priceByTradingRole(order?.priceInfo as PriceInfo | undefined, tradingRole);

  return {
    bargainId: toNum(bargain?.bargainId),
    specNumber: typeof bargain?.specNumber === 'string' ? bargain.specNumber : undefined,
    goodsType: typeof order?.goodsType === 'string' ? order.goodsType : undefined,
    quality: formatRoomQuality(order?.goodsMainProperty as GoodsMainProperty | undefined),
    weightTon: toNum(order?.weightTon),
    pricePerTon: price.pricePerTon,
    priceLabel: price.priceLabel,
    toAddressTitle: typeof sale?.toAddressTitle === 'string' ? sale.toAddressTitle : undefined,
    distanceKm: toNum(sale?.distance),
    myRole,
    counterparty,
    membersById: members ?? {},
    writable: computeWritable(power, myUserId),
  };
}
